import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { TaskBodyProps } from './index';
import { fetchPatientCard } from '../../../services/patientCard';
import { useManufacturerStatus } from '../../../hooks/useManufacturerStatus';
import { isSparkManufacturer as checkIsSparkManufacturer } from '../../../utils/manufacturer';
import { copyToClipboard } from '../../../utils/clipboard';
import { FEATURE_FLAGS } from '../../../featureFlags';
import { FilesTabContent } from '../../patient-files/FilesTabContent';
import { SparkSubmissionWizard } from './spark/SparkSubmissionWizard';

const CARD_STALE_MS = 60_000;

function isValidUrl(s: string): boolean {
    try {
        new URL(s);
        return true;
    } catch {
        return false;
    }
}

function extractCaseIdFromUrl(url: string): string | null {
    try {
        const u = new URL(url);
        return (
            u.searchParams.get('CaseId') ??
            u.searchParams.get('caseId') ??
            u.searchParams.get('case_id') ??
            null
        );
    } catch {
        return null;
    }
}

function parseDobIso(iso: string | null): { yyyy: string; mm: string; dd: string } | null {
    if (!iso) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return null;
    return { yyyy: match[1] as string, mm: match[2] as string, dd: match[3] as string };
}

function formatDob(iso: string | null): string | null {
    const parts = parseDobIso(iso);
    if (!parts) return null;
    return `${parts.mm}/${parts.dd}/${parts.yyyy}`;
}

function ageFromIso(iso: string | null): number | null {
    const parts = parseDobIso(iso);
    if (!parts) return null;
    const yyyy = Number(parts.yyyy);
    const mm = Number(parts.mm);
    const dd = Number(parts.dd);
    const now = new Date();
    let age = now.getFullYear() - yyyy;
    const monthDiff = now.getMonth() + 1 - mm;
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dd)) age--;
    return age >= 0 ? age : null;
}

//  Reusable copy button

function CopyButton({
    value,
    label = 'Copy',
    className = ''
}: {
    value: string;
    label?: string;
    className?: string;
}) {
    const [copied, setCopied] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (timer.current !== null) clearTimeout(timer.current);
        },
        []
    );
    const onClick = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (!(await copyToClipboard(value))) return;
        setCopied(true);
        timer.current = setTimeout(() => setCopied(false), 1800);
    };
    const baseClass =
        'inline-flex items-center gap-1 px-2 py-[3px] rounded-md border text-[11px] font-medium transition-colors whitespace-nowrap';
    const stateClass = copied
        ? 'border-green-500 text-green-700 bg-green-50'
        : 'border-gray-300 text-gray-500 bg-white hover:border-blue-500 hover:text-blue-600';
    return (
        <button
            type="button"
            onClick={onClick}
            className={`${baseClass} ${stateClass} ${className}`}
        >
            {copied ? (
                <>
                    <CheckIcon className="w-3 h-3" /> Copied
                </>
            ) : (
                label
            )}
        </button>
    );
}

const ROW_LABEL_W = 'w-[88px]';

function PatientRow({
    label,
    value,
    copyValue,
    isLast
}: {
    label: string;
    value: string;
    copyValue?: string;
    isLast?: boolean;
}) {
    return (
        <div
            className={`flex items-center justify-between py-1.5 ${isLast ? '' : 'border-b border-gray-100'}`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <span
                    className={`text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${ROW_LABEL_W} flex-shrink-0`}
                >
                    {label}
                </span>
                <span className="text-xs text-gray-800 truncate">{value}</span>
            </div>
            <CopyButton value={copyValue ?? value} />
        </div>
    );
}

function CasePillRow({
    label,
    value,
    isLast
}: {
    label: string;
    value: string | null;
    isLast?: boolean;
}) {
    return (
        <div
            className={`flex items-center justify-between gap-3 py-1.5 ${isLast ? '' : 'border-b border-gray-100'}`}
        >
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <span
                    className={`text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${ROW_LABEL_W} flex-shrink-0`}
                >
                    {label}
                </span>
                {value ? (
                    <span className="inline-flex items-center px-2 py-[1px] rounded-full text-[11px] font-medium bg-blue-100 text-blue-800 whitespace-nowrap truncate">
                        {value}
                    </span>
                ) : (
                    <span className="text-xs text-gray-300 italic">-</span>
                )}
            </div>
            {value && <CopyButton value={value} />}
        </div>
    );
}

function CaseTextRow({
    label,
    value,
    isLast
}: {
    label: string;
    value: string | null;
    isLast?: boolean;
}) {
    return (
        <div
            className={`flex items-start justify-between gap-3 py-1.5 ${isLast ? '' : 'border-b border-gray-100'}`}
        >
            <div className="flex items-start gap-3 min-w-0 flex-1">
                <span
                    className={`text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${ROW_LABEL_W} flex-shrink-0 mt-0.5`}
                >
                    {label}
                </span>
                {value ? (
                    <span className="text-xs text-gray-800 flex-1 min-w-0 break-words leading-relaxed line-clamp-3">
                        {value}
                    </span>
                ) : (
                    <span className="text-xs text-gray-300 italic mt-0.5">-</span>
                )}
            </div>
            {value && (
                <div className="mt-0.5">
                    <CopyButton value={value} />
                </div>
            )}
        </div>
    );
}

//  Cell chrome - each grid cell is a white card with sticky header + scrolling body

function Cell({
    title,
    right,
    children,
    scroll = true
}: {
    title: string;
    right?: React.ReactNode;
    children: React.ReactNode;
    scroll?: boolean;
}) {
    return (
        <div className="flex-1 min-w-0 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <span className="font-bold text-sm text-gray-800">{title}</span>
                {right}
            </div>
            <div
                className={`flex-1 min-h-0 ${scroll ? 'overflow-y-auto scrollbar-hide px-5 py-3' : 'flex flex-col'}`}
            >
                {children}
            </div>
        </div>
    );
}

interface PatientField {
    label: string;
    value: string;
    copyValue?: string;
}

export function SubmitToManufacturerTask(props: TaskBodyProps) {
    const { workCase: c } = props;
    const { data: card, isLoading } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: CARD_STALE_MS,
        enabled: Boolean(c.id)
    });

    const isSparkManufacturer = checkIsSparkManufacturer(card?.treatment?.manufacturerSlug ?? null);
    // The guided wizard is gated behind a dark-launch flag (opt-in, disabled by
    // default); when off, Spark practices get the manual submission fallback.
    const wizardEligible = FEATURE_FLAGS.SPARK_CREATE_CASE_WIZARD_ENABLED && isSparkManufacturer;

    // Only probe readiness when the wizard could actually render — it needs both
    // a Spark case AND a usable Spark integration for the practice. Invisalign
    // cases have no API client at all, so they always take the manual flow.
    const { automationReady, isLoading: statusLoading } = useManufacturerStatus(c.id, {
        enabled: wizardEligible
    });

    const waitingForCard = isLoading && !card;
    if (waitingForCard || statusLoading) {
        return (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                Loading…
            </div>
        );
    }

    if (wizardEligible && automationReady) {
        return <SparkSubmissionWizard {...props} />;
    }
    // Spark manufacturer but the integration isn't usable for this practice yet:
    // fall back to manual submission and flag why the guided wizard is hidden.
    return <ManualSubmitToManufacturer {...props} sparkNotInitialized={isSparkManufacturer} />;
}

function ManualSubmitToManufacturer({
    workCase: c,
    onPayloadChange
    // sparkNotInitialized = false,
}: TaskBodyProps & { sparkNotInitialized?: boolean }) {
    const [caseId, setCaseId] = useState('');
    const [link, setLink] = useState('');
    const [prefilled, setPrefilled] = useState(false);

    const trimmedCaseId = caseId.trim();
    const trimmedLink = link.trim();
    const linkLooksValid = trimmedLink.length > 0 && isValidUrl(trimmedLink);
    const caseIdLooksLikeUrl = /^https?:\/\//i.test(trimmedCaseId);
    const linkCaseId = linkLooksValid ? extractCaseIdFromUrl(trimmedLink) : null;
    const caseIdMismatch =
        trimmedCaseId.length > 0 && linkCaseId !== null && linkCaseId !== trimmedCaseId;

    const valid = trimmedCaseId.length > 0 && linkLooksValid && !caseIdLooksLikeUrl;

    useEffect(() => {
        onPayloadChange?.({
            payload: valid
                ? { manufacturerCaseId: trimmedCaseId, manufacturerLink: trimmedLink }
                : null,
            valid
        });
    }, [trimmedCaseId, trimmedLink, valid, onPayloadChange]);

    const linkError = trimmedLink.length > 0 && !linkLooksValid;

    const { data: card } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: CARD_STALE_MS,
        enabled: Boolean(c.id)
    });

    // Pre-fill fields from the DB when the card first loads (e.g. returning to
    // this step after it was previously completed).
    useEffect(() => {
        if (!card || prefilled) return;
        const savedCaseId = card.treatment?.manufacturerCaseId ?? '';
        const savedLink = card.treatment?.manufacturerLink ?? '';
        if (savedCaseId) setCaseId(savedCaseId);
        if (savedLink) setLink(savedLink);
        setPrefilled(true);
    }, [card, prefilled]);

    const fullName = card
        ? `${card.lastName ?? ''}, ${card.firstName ?? ''}`.replace(/^,\s*|,\s*$/g, '') || card.name
        : '';
    const dobIso = card?.dateOfBirth ?? null;
    const dobFormatted = formatDob(dobIso);
    const age = ageFromIso(dobIso);
    // Display = "11/12/1990 (35 yo)" for context; copyValue = "11/12/1990"
    const dobDisplay = dobFormatted
        ? age !== null
            ? `${dobFormatted} (${age} yo)`
            : dobFormatted
        : '';
    const leftFields: PatientField[] = [
        { label: 'Name', value: fullName },
        { label: 'DOB', value: dobDisplay, copyValue: dobFormatted ?? '' },
        { label: 'Gender', value: card?.gender ?? '' },
        { label: 'Email', value: card?.email ?? '' }
    ].filter((f) => f.value.length > 0);
    const phone = card?.phone ?? '';

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {/*
      {sparkNotInitialized && (
        <div className="flex items-start gap-2 mx-4 mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-400">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <span className="font-bold uppercase tracking-wide text-[10px] mr-1.5">The guided Spark wizard is unavailable.</span>
            Submit the case manually below in the meantime.
          </div>
        </div>
      )}
      */}
            {/* 2x2 grid - 30/70 vertical, 50/50 horizontal */}
            <div className="flex-1 min-h-0 flex flex-col gap-3 px-4 pt-3 pb-3">
                {/* TOP ROW - sizes to content so all 8 rows are always visible. Bottom
            row flex-1 takes whatever remains. */}
                <div className="flex-shrink-0">
                    <Cell title="Patient Info">
                        <div className="grid grid-cols-2 gap-x-4 font-inter">
                            {/* Left column - identity */}
                            <div className="flex flex-col">
                                {leftFields.map((f, i) => (
                                    <PatientRow
                                        key={f.label}
                                        label={f.label}
                                        value={f.value}
                                        copyValue={f.copyValue}
                                        isLast={i === leftFields.length - 1}
                                    />
                                ))}
                            </div>

                            {/* Right column - contact + case */}
                            <div className="flex flex-col">
                                <PatientRow label="Phone" value={phone} />
                                <CasePillRow
                                    label="Aligner"
                                    value={card?.treatment?.manufacturer ?? null}
                                />
                                <CaseTextRow
                                    label="Doctor notes"
                                    value={card?.notes?.doctorGoals ?? null}
                                />
                                <CaseTextRow
                                    label="Practice notes"
                                    value={card?.treatment?.additionalInfo ?? null}
                                    isLast
                                />
                            </div>
                        </div>
                    </Cell>
                </div>

                {/* BOTTOM ROW - takes remaining vertical space */}
                <div className="flex-1 min-h-0 flex gap-3">
                    <Cell title="Files" scroll={false}>
                        <FilesTabContent patientId={c.id} />
                    </Cell>

                    <Cell title="Steps to follow">
                        {/* 2x2 grid of step chips - balanced, compact, fits any width. */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 pb-3 border-b border-gray-100 font-inter">
                            {[
                                'Open Manufacturer portal',
                                'Create patient using the info above',
                                'Upload the required files',
                                'Paste the reference back'
                            ].map((title, i) => (
                                <div key={i} className="flex items-center gap-1.5 min-w-0">
                                    <span className="w-5 h-5 rounded-full bg-gray-800 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                        {i + 1}
                                    </span>
                                    <span className="text-xs text-gray-700 truncate">{title}</span>
                                </div>
                            ))}
                        </div>

                        {/* Form inputs - always visible below the checklist */}
                        <div className="flex flex-col gap-3 font-inter">
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                    Manufacturer case ID
                                </label>
                                <input
                                    type="text"
                                    value={caseId}
                                    onChange={(e) => setCaseId(e.target.value)}
                                    placeholder="e.g. 1272983"
                                    className={`w-full px-3 py-2 rounded-md text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                                        caseIdLooksLikeUrl
                                            ? 'border border-red-300 focus:border-red-400'
                                            : trimmedCaseId
                                              ? 'border border-green-500 bg-green-50 focus:border-green-600'
                                              : 'border border-gray-300 focus:border-[rgb(55,115,168)]'
                                    }`}
                                />
                                {caseIdLooksLikeUrl && (
                                    <p className="mt-1 text-xs text-red-500">
                                        Looks like a URL - paste the link in the Manufacturer
                                        patient link field below instead.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                                    Manufacturer patient link
                                </label>
                                <input
                                    type="url"
                                    value={link}
                                    onChange={(e) => setLink(e.target.value)}
                                    placeholder="https://aligners.ormcodigital.com/getPatientDetails?CaseId=1272983"
                                    className={`w-full px-3 py-2 rounded-md text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                                        linkError
                                            ? 'border border-red-300 focus:border-red-400'
                                            : caseIdMismatch
                                              ? 'border border-amber-400 focus:border-amber-500'
                                              : trimmedLink && linkLooksValid
                                                ? 'border border-green-500 bg-green-50 focus:border-green-600'
                                                : 'border border-gray-300 focus:border-[rgb(55,115,168)]'
                                    }`}
                                />
                                {linkError && (
                                    <p className="mt-1 text-xs text-red-500">Enter a valid URL.</p>
                                )}
                                {!linkError && caseIdMismatch && linkCaseId && (
                                    <div className="mt-2 flex items-start gap-2 px-2.5 py-2 rounded-md bg-amber-50 border border-amber-400">
                                        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-xs text-amber-900 leading-relaxed">
                                            <span className="font-bold uppercase tracking-wide text-[10px] mr-1.5">
                                                Warning
                                            </span>
                                            Link's CaseId is <strong>{linkCaseId}</strong> but you
                                            entered <strong>{trimmedCaseId}</strong>. You can still
                                            submit if this is intentional.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Cell>
                </div>
            </div>
        </div>
    );
}
